<template>
	<!-- Files on a work, kept in storage: the measurement sheet as links, photos of the site as thumbnails -->
	<div class="flex flex-col gap-4">
		<div v-for="k in shownKinds" :key="k.kind">
			<!-- the links carry their own padding; the thumbnails need the room -->
			<div class="flex flex-row items-center gap-2" :class="k.kind === 'imagine' ? 'mb-3' : 'mb-1'">
				<v-label>{{ k.title }} ({{ filesOf(k.kind).length }}/{{ k.max }})</v-label>
				<v-spacer></v-spacer>
				<template v-if="editable">
					<input :ref="'input-' + k.kind" type="file" class="hidden" :accept="k.accept" :multiple="k.max > 1"
						@change="pick(k, $event)">
					<v-btn x-small text color="teal" :disabled="!canAdd(k)"
						@click="$refs['input-' + k.kind][0].click()">
						<v-icon left small>mdi-camera-plus</v-icon>
						Adauga
					</v-btn>
				</template>
			</div>
			<p v-if="!filesOf(k.kind).length && !uploadsOf(k.kind).length" class="text-sm text-gray-600 mb-0">
				{{ k.empty }}</p>

			<!-- the measurement sheet: plain links -->
			<template v-if="k.kind === 'masuratori'">
				<!-- keys prefixed: files and uploads share this list, and file 1 and upload 1 would clash -->
				<div v-for="f in filesOf(k.kind)" :key="'file-' + f.id_file" class="flex flex-row items-center gap-1 py-1"
					:title="`${f.created_by_name || '-'} · ${formatDateTime(f.created_at)}`">
					<v-icon small>{{ f.content_type === "application/pdf" ? "mdi-file-pdf-box" : "mdi-file-image"
						}}</v-icon>
					<a :href="f.url" target="_blank" rel="noopener" class="truncate">{{ f.name }}</a>
					<v-btn v-if="canDelete(f)" icon x-small title="Sterge" @click="remove(f)">
						<v-icon x-small>mdi-close</v-icon>
					</v-btn>
					<v-icon v-else-if="locked" x-small
						title="Masuratorile sunt confirmate: documentul nu se mai poate sterge">mdi-lock</v-icon>
				</div>
				<div v-for="u in uploadsOf(k.kind)" :key="'upload-' + u.id" class="flex flex-row items-center gap-1 py-1">
					<v-progress-circular :value="u.progress" :indeterminate="!u.progress" color="teal" size="16"
						width="2"></v-progress-circular>
					<span class="truncate text-gray-600">{{ u.name }}</span>
				</div>
			</template>

			<!-- photos: thumbnails -->
			<div v-else class="files">
				<div v-for="f in filesOf(k.kind)" :key="'file-' + f.id_file" class="file"
					:title="`${f.name}\n${f.created_by_name || '-'} · ${formatDateTime(f.created_at)}`">
					<a :href="f.url" target="_blank" rel="noopener" class="file__link">
						<img v-if="isPicture(f)" :src="f.url" :alt="f.name" class="file__img" loading="lazy">
						<div v-else class="file__other">
							<v-icon>mdi-file-image</v-icon>
						</div>
					</a>
					<v-btn v-if="canDelete(f)" icon x-small class="file__delete" title="Sterge" @click="remove(f)">
						<v-icon x-small>mdi-close</v-icon>
					</v-btn>
				</div>
				<div v-for="u in uploadsOf(k.kind)" :key="'upload-' + u.id" class="file file__other" :title="u.name">
					<v-progress-circular :value="u.progress" :indeterminate="!u.progress" color="teal"
						size="24"></v-progress-circular>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import moment from "moment";
import { stageIndex } from "@/utils/works";

// The API's limits (WorksService::FILE_KINDS), to say so before uploading.
const KINDS = [
	{
		kind: "masuratori",
		title: "Fisa masuratori",
		max: 2,
		// on a phone image/* offers the camera: mostly a photo of the paper sheet
		accept: "image/*,application/pdf",
		empty: "Fotografiaza fisa de masuratori sau incarca PDF.",
	},
	{
		kind: "imagine",
		title: "Imagini infrastructura existenta",
		max: 10,
		accept: "image/*",
		empty: "Nicio imagine.",
	},
];

let uploadSeq = 0;

export default {
	name: "WorkFiles",
	props: {
		work: { type: Object, required: true },
		kinds: { type: Array, default: () => ["masuratori", "imagine"] },
		// may add files and delete them
		editable: { type: Boolean, default: false },
	},
	data() {
		return {
			uploads: [],
		};
	},
	computed: {
		shownKinds() {
			return KINDS.filter((k) => this.kinds.includes(k.kind));
		},
		// the measurements are confirmed: the sheet production works from stays
		locked() {
			return stageIndex(this.work.stage) >= stageIndex("gata_productie");
		},
	},
	methods: {
		filesOf(kind) {
			return (this.work.files || []).filter((f) => f.kind === kind);
		},
		uploadsOf(kind) {
			return this.uploads.filter((u) => u.kind === kind);
		},
		canAdd(k) {
			return this.filesOf(k.kind).length + this.uploadsOf(k.kind).length < k.max;
		},
		canDelete(f) {
			return this.editable && !(f.kind === "masuratori" && this.locked);
		},
		// browsers outside Safari cannot show HEIC: a file tile instead
		isPicture(f) {
			return f.url && f.content_type.startsWith("image/") && !/hei[cf]$/.test(f.content_type);
		},
		formatDateTime(value) {
			return value ? moment(value).format("DD.MM.YYYY HH:mm") : "-";
		},
		// The measurement sheet is saved as fisa_masuratori-<client>, -2 for a second one, so it
		// is recognisable once downloaded. Photos keep the phone's name.
		baseName(kind) {
			if (kind !== "masuratori") return null;
			const client = (this.work.client_nume || "")
				.normalize("NFD")
				.replace(/[\u0300-\u036f]/g, "")
				.toLowerCase()
				.replace(/[^a-z0-9]+/g, "_")
				.replace(/^_+|_+$/g, "");
			const taken = (this.work.files || []).map((f) => f.name.replace(/\.[^.]+$/, ""))
				.concat(this.uploads.map((u) => u.name));
			const base = "fisa_masuratori" + (client ? "-" + client : "");
			let name = base;
			for (let n = 2; taken.includes(name); n++) name = `${base}-${n}`;
			return name;
		},
		async pick(k, event) {
			const room = k.max - this.filesOf(k.kind).length - this.uploadsOf(k.kind).length;
			const files = Array.from(event.target.files || []);
			event.target.value = null;
			if (files.length > room) {
				M.toast({ html: `Se mai pot adauga ${room} fisiere`, classes: "orange darken-2" });
			}
			// one at a time: the API counts each against the limit
			for (const file of files.slice(0, room)) {
				const baseName = this.baseName(k.kind);
				const upload = { id: ++uploadSeq, kind: k.kind, name: baseName || file.name, progress: 0 };
				this.uploads.push(upload);
				try {
					await this.$store.dispatch("works/uploadWorkFile", {
						workId: this.work.id_work,
						kind: k.kind,
						file,
						baseName,
						onProgress: (p) => (upload.progress = p),
					});
				} catch (e) {
					// toast already shown by the store
				} finally {
					this.uploads = this.uploads.filter((u) => u.id !== upload.id);
				}
			}
		},
		remove(f) {
			if (!confirm(`Stergi ${f.name}?`)) return;
			this.$store.dispatch("works/deleteWorkFile", { workId: this.work.id_work, id: f.id_file }).catch(() => { });
		},
	},
};
</script>

<style scoped>
.files {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(64px, 1fr));
	gap: 8px;
}

.file {
	position: relative;
	aspect-ratio: 1;
	border: 1px solid #e0e0e0;
	border-radius: 4px;
	overflow: hidden;
	background: #fafafa;
}

.file__link {
	display: block;
	width: 100%;
	height: 100%;
	color: inherit;
}

.file__img {
	width: 100%;
	height: 100%;
	object-fit: cover;
}

.file__other {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	height: 100%;
	padding: 4px;
}

.file__delete {
	position: absolute;
	top: 2px;
	right: 2px;
	background: rgba(255, 255, 255, 0.85);
	border-radius: 50%;
}
</style>
